import {
    createContext,
    FormEvent,
    memo,
    MutableRefObject,
    useEffect,
    useRef,
    useState,
} from 'react';
import { default as ReactGA4 } from 'react-ga4';
import { useBoolean, useDebounce } from 'react-use';
import { useRouter } from 'next/router';
import cn from 'classnames';
import { ArrayUtils, StringUtils } from 'src/utils';
import { useOutsideClick, useFocus } from 'src/hooks';
import { useAsyncFn } from 'src/hooks/useAsyncFn';
import { NO_FOCUS } from 'src/hooks/useFocus';
import { SearchServices } from 'src/services/SearchService';
import { Container, Element, IconButton } from 'src/components';
import SearchDropdown from './SearchDropdown';

import styles from './search.module.scss';

const DEFAULT_SUGGEST = [] as any;

export const SearchDropdownContext = createContext(0);

function Search() {
    const router = useRouter();
    const routerKeyword = router.query?.keyword || '';
    const searchRef: MutableRefObject<HTMLElement | null> =
        useRef<HTMLElement>(null);
    const [topicState, topicFn] = useAsyncFn(SearchServices.searchTopic);
    const [contentState, contentFn] = useAsyncFn(SearchServices.searchContent);
    const [keyword, setKeyword] = useState<string>('');
    const [show, setShow] = useBoolean(false);
    const [suggest, setSuggest] = useState(DEFAULT_SUGGEST);
    const [isTracking, setTracking] = useState<boolean>(false);
    const currFocus = useFocus(suggest);

    const handleClickOutside = (isInside: boolean) => {
        if (!isInside) {
            setShow(false);
        }
    };

    const searchDebounce = () => {
        if (keyword.length && show) {
            topicFn(keyword);
            contentFn(keyword);
        } else {
            resetSuggest();
        }
    };

    useOutsideClick(searchRef, handleClickOutside);
    useDebounce(searchDebounce, 200, [keyword]);

    useEffect(() => {
        if (routerKeyword) {
            setKeyword(StringUtils.formatQuerySearch(routerKeyword as string));
        }
    }, [routerKeyword]);

    useEffect(() => {
        if (contentState.value && topicState.value) {
            const contents = contentState.value?.article || [];
            const topics =
                topicState.value?.topic?.map((item: any) => ({
                    ...item,
                    isTopic: true,
                })) || [];

            setSuggest([...topics.slice(0, 1), ...contents.slice(0, 3)]);
        }
    }, [contentState.value, topicState.value]);

    const resetSuggest = () => {
        setTimeout(() => {
            setSuggest(DEFAULT_SUGGEST);
        }, 10);
    };

    const clickSubmit = (e: any) => {
        ReactGA4.event({
            category: 'mobile_search',
            action: 'clickButtonSearch',
        });
        handleSubmit(e);
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!keyword) {
            alert('Bạn phải gõ từ khoá tìm kiếm');
            return false;
        }

        if (currFocus !== NO_FOCUS && currFocus < suggest.length) {
            const currItem = suggest[currFocus];

            if (currItem.isTopic) {
                router.push(`/t${currItem.id}.epi`);
            } else {
                router.push(`/c${currItem.id}.epi`);
            }
        } else {
            router.push(
                `/tim-kiem/${StringUtils.formatSearchStr(keyword)}.epi`
            );
        }
        resetSuggest();
        setShow(false);
        return false;
    };

    const handleInput = (e: FormEvent<HTMLInputElement>) => {
        e.preventDefault();
        setKeyword(e.currentTarget.value);
    };

    const handleFocus = () => {
        setShow(true);
    };

    const handleBlur = (e: any) => {
        if (e.keyCode === 13) {
            e.target.blur();
            handleSubmit(e);
        }
    };

    useEffect(() => {
        if (!ArrayUtils.isEmpty(suggest) && !isTracking) {
            ReactGA4.event({
                category: 'mobile_search',
                action: 'typeSearch',
            });
            setTracking(true);
        }
    }, [suggest, isTracking]);

    useEffect(() => {
        setTracking(false);
    }, [router.asPath]);

    return (
        <SearchDropdownContext.Provider value={currFocus as number}>
            <Element
                className={cn(
                    'search-box',
                    'p-[16px]',
                    'mt-[-15px] mb-[15px]',
                    'bg-[#f9fafb]',
                    'border-b border-solid border-[#dee2e6]'
                )}
            >
                <Container className="max-md:p-0">
                    <form onSubmit={handleSubmit}>
                        <Element
                            domRef={searchRef}
                            className={cn(
                                'bm-search-container',
                                'flex relative',
                                'w-full h-[38px]',
                                'ml-0',
                                'rounded-[3px]',
                                'border border-solid border-[#e9ecef]',
                                'box-content',
                            )}
                        >
                            <input
                                type="text"
                                className={cn(
                                    'grow',
                                    'text-[1.8rem] text-[#333] font-semibold',
                                    'p-0 pl-[10px] mr-[48px]',
                                    'bg-white',
                                    'placeholder:text-[1.3rem] placeholder:leading-[48px] placeholder:font-normal',
                                )}
                                placeholder="Nhập nội dung tìm kiếm"
                                onFocus={handleFocus}
                                value={keyword}
                                onChange={handleInput}
                                onKeyDown={handleBlur}
                            />
                            <IconButton
                                className={cn(
                                    'flex items-center justify-center',
                                    'w-[48px]',
                                    'text-[2rem] text-white',
                                    'bg-[var(--primary)]'
                                )}
                                onClick={clickSubmit}
                                icon="search"
                            />
                            <SearchDropdown
                                keyword={keyword}
                                resetSuggest={resetSuggest}
                                suggest={suggest}
                                show={show}
                            />
                        </Element>
                    </form>
                </Container>
            </Element>
        </SearchDropdownContext.Provider>
    );
}

export default memo(Search);
